import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { buildAuthFailureResult } from '../utils/authFlow';

/* Exported so a caller that is not the app can supply the shape — the
 * marketing homepage mounts real product pages against a demo user. */
export const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data.data?.user || response.data.user || response.data;
      setUser(userData);
      setLoading(false);
    } catch (error) {
      // Only remove token if we got a 401, not for network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
      }
      // Always set loading to false even on network errors
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const responseData = response.data.data?.data || response.data.data || response.data;
      const token = responseData.token;
      const tenantApiKey = responseData.tenantApiKey;
      const user = responseData.user;

      if (!token) {
        throw new Error('No authentication token received');
      }

      localStorage.setItem('token', token);
      if (tenantApiKey) {
        localStorage.setItem('tenantApiKey', tenantApiKey);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setToken(token);
      setUser(user);
      
      // If user data is not in the response, fetch it
      if (!user) {
        await fetchUser();
      }

      toast.success('Welcome back!');
      return { success: true };
    } catch (error) {
      const result = buildAuthFailureResult(error, email);
      toast.error(result.error);
      return result;
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      const responseData = response.data.data?.data || response.data.data || response.data;
      const authToken = responseData.token;
      const tenantApiKey = responseData.tenantApiKey;
      const user = responseData.user;

      if (!authToken) {
        throw new Error('No authentication token received');
      }

      localStorage.setItem('token', authToken);
      if (tenantApiKey) {
        localStorage.setItem('tenantApiKey', tenantApiKey);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      setToken(authToken);
      setUser(user);

      if (!user) {
        await fetchUser();
      }

      toast.success('Signed in with Google');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Google sign-in failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const responseData = response.data.data || response.data;
      const message = responseData?.message || 'Account created. Check your email to verify your account.';

      localStorage.removeItem('token');
      localStorage.removeItem('tenantApiKey');
      delete api.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);

      toast.success(message);
      return { success: true, message };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenantApiKey');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
